import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import Countdown from "@/components/Countdown";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { PlayCircle, Calendar, BookOpen, Bell, Trophy, FileText, Award, Receipt, Users, ExternalLink, MapPin, Download, ListChecks, Sparkles, Check, X as XIcon, User, Share2, MessageCircle, Bookmark, ScanLine } from "lucide-react";
import DoubtBotWidget from "@/components/DoubtBotWidget";
import { MyProfileTab, SharesTab } from "@/components/student/StudentExtras";
import { ChatTab, MyBookmarksTab } from "@/components/engagement/EngagementComponents";
import { ScanQRTab } from "@/components/sprintc/SprintCComponents";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

export default function StudentDashboard() {
  const { user } = useAuth();
  const [enrollments, setEnrollments] = useState([]);
  const [upcoming, setUpcoming] = useState([]);
  const [mySchedule, setMySchedule] = useState([]);
  const [recordings, setRecordings] = useState([]);
  const [assignments, setAssignments] = useState([]);
  const [quizzes, setQuizzes] = useState([]);
  const [leaderboard, setLeaderboard] = useState({ leaderboard: [], my_row: null });
  const [notifications, setNotifications] = useState([]);
  const [batches, setBatches] = useState({});
  const [payments, setPayments] = useState([]);
  const [submitAsgn, setSubmitAsgn] = useState(null);
  const [takeQuiz, setTakeQuiz] = useState(null);

  const reload = () => {
    api.get("/enrollments").then(async ({ data }) => {
      setEnrollments(data);
      const ids = [...new Set(data.map((e) => e.batch_id).filter(Boolean))];
      const map = {};
      await Promise.all(ids.map(async (bid) => {
        try { const { data: b } = await api.get(`/batches/${bid}`); map[bid] = b; } catch (_) {}
      }));
      setBatches(map);
    });
    api.get("/classes/upcoming").then(({ data }) => setUpcoming(data));
    api.get("/my-schedule").then(({ data }) => setMySchedule(data.classes || []));
    api.get("/recordings").then(({ data }) => setRecordings(data));
    api.get("/assignments").then(({ data }) => setAssignments(data));
    api.get("/quizzes").then(({ data }) => setQuizzes(data));
    api.get("/leaderboard").then(({ data }) => {
      // Backend may return either {leaderboard:[], my_row:null} OR (legacy) a bare array
      if (Array.isArray(data)) setLeaderboard({ leaderboard: data, my_row: null });
      else setLeaderboard({ leaderboard: data?.leaderboard || [], my_row: data?.my_row || null });
    }).catch(() => {});
    api.get("/notifications").then(({ data }) => setNotifications(data));
    api.get("/payments").then(({ data }) => setPayments(data));
  };

  useEffect(() => { reload(); }, []);

  const downloadInvoice = (orderId) => {
    const token = localStorage.getItem("cwk_token");
    fetch(`${BACKEND}/api/payments/${orderId}/invoice`, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => { if (!r.ok) throw new Error("Invoice unavailable"); return r.blob(); })
      .then((blob) => {
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = `invoice-${orderId.slice(-12)}.pdf`;
        a.click();
        URL.revokeObjectURL(url);
      })
      .catch((e) => toast.error(e.message));
  };

  const downloadCertificate = (enrollmentId) => {
    const token = localStorage.getItem("cwk_token");
    fetch(`${BACKEND}/api/enrollments/${enrollmentId}/certificate`, { headers: { Authorization: `Bearer ${token}` } })
      .then(async (r) => {
        if (!r.ok) {
          const err = await r.json().catch(() => ({}));
          throw new Error(err.detail || "Certificate unavailable");
        }
        return r.blob();
      })
      .then((blob) => {
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = `certificate-${enrollmentId.slice(0, 8)}.pdf`;
        a.click();
        URL.revokeObjectURL(url);
      })
      .catch((e) => toast.error(e.message));
  };

  const next = upcoming[0];
  const primaryBatch = Object.values(batches)[0];

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 py-10" data-testid="student-dashboard">
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-8">
        <div>
          <div className="text-xs uppercase tracking-[0.18em] text-fuchsia-300">Student Dashboard</div>
          <h1 className="mt-1 font-display text-3xl sm:text-4xl font-semibold">Hi {user?.name?.split(" ")[0]} 👋</h1>
        </div>
        <div className="flex flex-wrap gap-3 text-sm">
          <Stat label="Enrolled" value={enrollments.length} />
          <Stat label="Upcoming" value={upcoming.length} />
          <Stat label="Recordings" value={recordings.length} />
        </div>
      </div>

      {/* MY BATCH HERO */}
      {primaryBatch && (
        <div className="glass-strong rounded-2xl p-5 sm:p-6 aurora relative overflow-hidden mb-6" data-testid="my-batch-hero">
          <div className="flex flex-col lg:flex-row gap-5 justify-between">
            <div>
              <div className="text-xs uppercase tracking-[0.18em] text-fuchsia-300">My batch</div>
              <div className="mt-1 font-display text-xl sm:text-2xl font-semibold">{primaryBatch.name}</div>
              <div className="text-sm text-[var(--text-dim)] mt-1">{primaryBatch.course_title}</div>
              <div className="mt-3 grid sm:grid-cols-2 gap-2 text-sm text-[var(--text-dim)]">
                {primaryBatch.teacher_name && <Row icon={Users} text={`Teacher: ${primaryBatch.teacher_name}`} />}
                {primaryBatch.timing && <Row icon={Calendar} text={primaryBatch.timing} />}
                <Row icon={Calendar} text={`${primaryBatch.start_date} → ${primaryBatch.end_date}`} />
                {primaryBatch.meeting_provider && <Row icon={PlayCircle} text={primaryBatch.meeting_provider} />}
              </div>
            </div>
            <div className="flex flex-col gap-2 items-stretch sm:items-end">
              {primaryBatch.meeting_link && (
                <a href={primaryBatch.meeting_link} target="_blank" rel="noreferrer" className="btn-primary" data-testid="join-batch-meeting">
                  <PlayCircle className="w-4 h-4" /> Join {primaryBatch.meeting_provider}
                </a>
              )}
              <span className="text-xs text-[var(--text-mute)]">Status: <b className="text-fuchsia-300 uppercase">{primaryBatch.status}</b></span>
            </div>
          </div>
        </div>
      )}

      {next && (
        <div className="glass-strong rounded-2xl p-5 sm:p-6 relative overflow-hidden mb-8" data-testid="next-class-hero">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
            <div>
              <div className="live-chip"><span className="dot live-dot" /> Next live</div>
              <div className="mt-2 font-display text-xl sm:text-2xl font-semibold">{next.title}</div>
              <div className="text-sm text-[var(--text-dim)] mt-1">{next.course_title} • {next.teacher_name}</div>
              <div className="text-xs text-[var(--text-mute)] mt-2">{new Date(next.scheduled_at).toLocaleString()}</div>
            </div>
            <div className="flex flex-col items-start sm:items-end gap-3">
              <Countdown targetIso={next.scheduled_at} />
              <Link to={`/live/${next.id}`} className="btn-primary" data-testid="join-next-class">
                <PlayCircle className="w-4 h-4" /> Join class
              </Link>
            </div>
          </div>
        </div>
      )}

      <Tabs defaultValue="courses" className="w-full">
        <TabsList className="bg-transparent border border-white/10 rounded-full p-1 mb-6 overflow-x-auto flex-wrap">
          <TabsTrigger value="courses" data-testid="tab-courses"><BookOpen className="w-4 h-4 mr-1" /> Courses</TabsTrigger>
          <TabsTrigger value="schedule" data-testid="tab-schedule"><Calendar className="w-4 h-4 mr-1" /> Schedule</TabsTrigger>
          <TabsTrigger value="recordings" data-testid="tab-recordings"><PlayCircle className="w-4 h-4 mr-1" /> Recordings</TabsTrigger>
          <TabsTrigger value="assignments" data-testid="tab-assignments"><FileText className="w-4 h-4 mr-1" /> Assignments</TabsTrigger>
          <TabsTrigger value="quizzes" data-testid="tab-quizzes"><ListChecks className="w-4 h-4 mr-1" /> Quizzes</TabsTrigger>
          <TabsTrigger value="leaderboard" data-testid="tab-leaderboard"><Trophy className="w-4 h-4 mr-1" /> Leaderboard</TabsTrigger>
          <TabsTrigger value="certificates" data-testid="tab-certificates"><Award className="w-4 h-4 mr-1" /> Certificates</TabsTrigger>
          <TabsTrigger value="payments" data-testid="tab-payments"><Receipt className="w-4 h-4 mr-1" /> Payments</TabsTrigger>
          <TabsTrigger value="alerts" data-testid="tab-alerts"><Bell className="w-4 h-4 mr-1" /> Alerts</TabsTrigger>
          <TabsTrigger value="profile" data-testid="tab-profile"><User className="w-4 h-4 mr-1" /> My Profile</TabsTrigger>
          <TabsTrigger value="shares" data-testid="tab-shares"><Share2 className="w-4 h-4 mr-1" /> Shares</TabsTrigger>
          <TabsTrigger value="chat" data-testid="tab-chat"><MessageCircle className="w-4 h-4 mr-1" /> Chat</TabsTrigger>
          <TabsTrigger value="bookmarks" data-testid="tab-bookmarks"><Bookmark className="w-4 h-4 mr-1" /> Bookmarks</TabsTrigger>
          <TabsTrigger value="qr" data-testid="tab-qr"><ScanLine className="w-4 h-4 mr-1" /> Scan QR</TabsTrigger>
        </TabsList>

        <TabsContent value="courses">
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {enrollments.length === 0 && (
              <div className="col-span-full glass rounded-2xl p-8 text-center text-[var(--text-dim)]">
                You haven't enrolled in any course yet.
                <div className="mt-3"><Link to="/courses" className="btn-primary inline-flex">Explore courses</Link></div>
              </div>
            )}
            {enrollments.map((e) => {
              const b = batches[e.batch_id];
              const remainingPaise = e.amount_remaining_paise || 0;
              const remainingINR = remainingPaise / 100;
              const isPartial = e.payment_status === "partial" && remainingINR > 0;
              const installmentsLeft = Math.max(0, (e.installments_total || 1) - (e.installments_paid || 1));
              return (
                <div key={e.id} className="glass lift rounded-2xl p-5" data-testid={`enrollment-${e.id}`}>
                  <div className="text-xs text-fuchsia-300 uppercase tracking-wider">Enrolled</div>
                  <div className="font-display text-lg font-semibold mt-1">{e.course_title}</div>
                  {b && <div className="text-xs text-[var(--text-mute)] mt-1">Batch: {b.name}</div>}
                  <div className="mt-3 h-1.5 rounded-full bg-white/5 overflow-hidden">
                    <div className="h-full bg-gradient-to-r from-fuchsia-500 to-purple-500" style={{ width: `${e.progress}%` }} />
                  </div>
                  <div className="mt-1 text-xs text-[var(--text-mute)]">{e.progress}% complete</div>
                  {isPartial && (
                    <div className="mt-3 p-2.5 rounded-lg bg-amber-500/10 border border-amber-400/30" data-testid={`balance-due-${e.id}`}>
                      <div className="text-[10px] uppercase tracking-wider font-bold text-amber-300">Balance due</div>
                      <div className="text-sm text-white mt-0.5">₹{Number(remainingINR).toLocaleString("en-IN")} remaining {installmentsLeft > 0 && `· ${installmentsLeft} installment${installmentsLeft > 1 ? "s" : ""} left`}</div>
                      <button
                        onClick={async () => {
                          try {
                            const { data } = await api.post("/payments/pay-balance", { course_id: e.course_id });
                            if (data.already_paid) { toast.success("Already paid in full!"); return; }
                            if (!data.mock && window.Razorpay) {
                              const rzp = new window.Razorpay({
                                key: data.key_id, amount: data.amount, currency: data.currency, order_id: data.order_id,
                                name: "CodeWithKamlesh", description: `Balance payment — ${e.course_title}`,
                                theme: { color: "#FF0066" },
                                handler: async (resp) => {
                                  try {
                                    await api.post("/payments/verify", { razorpay_order_id: resp.razorpay_order_id, razorpay_payment_id: resp.razorpay_payment_id, razorpay_signature: resp.razorpay_signature });
                                    toast.success("Balance paid — thank you!");
                                    setTimeout(() => window.location.reload(), 1200);
                                  } catch { toast.error("Verification failed"); }
                                },
                              });
                              rzp.open();
                            } else {
                              toast.message("Demo balance payment confirming…");
                              await new Promise((r) => setTimeout(r, 800));
                              await api.post("/payments/verify", { razorpay_order_id: data.order_id, razorpay_payment_id: `pay_mock_${Date.now()}`, razorpay_signature: "mock_signature" });
                              toast.success("Demo balance paid!");
                              setTimeout(() => window.location.reload(), 1200);
                            }
                          } catch (err) { toast.error(err.response?.data?.detail || "Payment failed"); }
                        }}
                        className="mt-2 w-full inline-flex items-center justify-center gap-1.5 bg-[#FF0066] hover:bg-[#e6005c] text-white text-xs font-semibold rounded-full px-3 py-1.5"
                        data-testid={`pay-balance-btn-${e.id}`}
                      >
                        Pay balance now
                      </button>
                    </div>
                  )}
                  <div className="mt-4 flex gap-2 flex-wrap">
                    <Link to={`/courses/${e.course_id}`} className="text-xs text-fuchsia-300 hover:underline">Open course →</Link>
                    {b?.meeting_link && (
                      <a href={b.meeting_link} target="_blank" rel="noreferrer" className="text-xs text-purple-300 hover:underline inline-flex items-center gap-1">
                        Join {b.meeting_provider} <ExternalLink className="w-3 h-3" />
                      </a>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </TabsContent>

        <TabsContent value="schedule">
          <ScheduleView classes={mySchedule} />
        </TabsContent>

        <TabsContent value="recordings">
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {recordings.length === 0 && <Empty msg="No recordings yet." />}
            {recordings.map((r) => (
              <a key={r.id} href={r.video_url} target="_blank" rel="noreferrer" className="glass lift rounded-2xl overflow-hidden block">
                <div className="aspect-video bg-[var(--bg-elev-1)] relative">
                  {r.thumbnail ? <img src={r.thumbnail} className="w-full h-full object-cover" alt="" /> : null}
                  <div className="absolute inset-0 bg-black/30 flex items-center justify-center">
                    <PlayCircle className="w-10 h-10 text-white/90" />
                  </div>
                </div>
                <div className="p-3">
                  <div className="text-sm font-semibold line-clamp-1">{r.title}</div>
                  <div className="text-xs text-[var(--text-mute)] mt-0.5">{r.duration_minutes} min</div>
                </div>
              </a>
            ))}
          </div>
        </TabsContent>

        <TabsContent value="assignments">
          <div className="space-y-3">
            {assignments.length === 0 && <Empty msg="No assignments pending." />}
            {assignments.map((a) => {
              const sub = a.my_submission;
              return (
                <div key={a.id} className="glass rounded-xl p-4" data-testid={`asgn-${a.id}`}>
                  <div className="flex items-center justify-between gap-3 flex-wrap">
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-semibold">{a.title}</div>
                      <div className="text-xs text-[var(--text-mute)] mt-0.5">{a.course_title} {a.due_date ? `• Due ${new Date(a.due_date).toLocaleDateString()}` : ""} • Max marks {a.max_marks}</div>
                      <div className="text-xs text-[var(--text-dim)] mt-1.5">{a.description}</div>
                    </div>
                    <div className="text-right shrink-0">
                      {sub ? (
                        <>
                          <div className="text-xs"><span className="px-2 py-0.5 rounded-full bg-fuchsia-500/15 text-fuchsia-300">Submitted</span></div>
                          {sub.marks != null && <div className="text-sm font-semibold mt-1">{sub.marks}/{a.max_marks}</div>}
                        </>
                      ) : (
                        <button onClick={() => setSubmitAsgn(a)} className="btn-primary text-xs" data-testid={`submit-asgn-${a.id}`}>
                          <FileText className="w-4 h-4" /> Submit
                        </button>
                      )}
                    </div>
                  </div>
                  {sub?.feedback && <div className="mt-3 text-xs glass rounded-lg p-2"><b className="text-amber-300">Feedback:</b> {sub.feedback}</div>}
                </div>
              );
            })}
          </div>
        </TabsContent>

        <TabsContent value="quizzes">
          <div className="space-y-3">
            {quizzes.length === 0 && <Empty msg="No quizzes available yet." />}
            {quizzes.map((q) => {
              const att = q.my_attempt;
              return (
                <div key={q.id} className="glass rounded-xl p-4 flex items-center justify-between gap-3 flex-wrap" data-testid={`quiz-${q.id}`}>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-semibold">{q.title}</div>
                    <div className="text-xs text-[var(--text-mute)] mt-0.5">{q.course_title} • {q.questions.length} questions • {q.duration_minutes} min • Pass {q.pass_percentage}%</div>
                    {q.description && <div className="text-xs text-[var(--text-dim)] mt-1.5">{q.description}</div>}
                  </div>
                  <div className="text-right shrink-0">
                    {att ? (
                      <>
                        <div className="text-sm font-semibold">{att.score}/{att.total_marks} <span className="text-xs">({att.percentage}%)</span></div>
                        <div className="text-xs mt-1">{att.passed ? <span className="text-fuchsia-300">✓ Passed</span> : <span className="text-amber-300">Try again</span>}</div>
                        <button onClick={() => setTakeQuiz(q)} className="btn-ghost text-xs mt-2" data-testid={`retake-quiz-${q.id}`}>Retake</button>
                      </>
                    ) : (
                      <button onClick={() => setTakeQuiz(q)} className="btn-primary text-xs" data-testid={`take-quiz-${q.id}`}>
                        <ListChecks className="w-4 h-4" /> Start quiz
                      </button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </TabsContent>

        <TabsContent value="leaderboard">
          <div className="glass rounded-2xl p-5">
            <div className="text-xs uppercase tracking-wider text-fuchsia-300 mb-3">Combined ranking — quizzes + assignments + attendance</div>
            {leaderboard.my_row && (
              <div className="glass-strong rounded-xl p-3 mb-4 border border-fuchsia-400/30 flex items-center justify-between" data-testid="my-rank">
                <div className="flex items-center gap-3">
                  <div className="w-9 h-9 rounded-full bg-gradient-to-br from-fuchsia-500 to-purple-600 flex items-center justify-center font-bold">#{leaderboard.my_row.rank}</div>
                  <div>
                    <div className="text-sm font-semibold">You — {leaderboard.my_row.user_name}</div>
                    <div className="text-xs text-[var(--text-mute)]">Quiz avg {leaderboard.my_row.quiz_avg}% • Asgn avg {leaderboard.my_row.asg_avg} • Attendance {leaderboard.my_row.attendance}</div>
                  </div>
                </div>
                <div className="font-display text-2xl font-semibold text-amber-300">{leaderboard.my_row.score}</div>
              </div>
            )}
            {leaderboard.leaderboard.length === 0 && <Empty msg="Leaderboard khaali hai — quizzes attempt karke top karo!" />}
            <div className="space-y-2">
              {leaderboard.leaderboard.map((r) => (
                <div key={r.user_id} className={`glass rounded-lg p-3 flex items-center gap-3 ${r.user_id === user?.id ? "border border-fuchsia-400/30" : ""}`} data-testid={`lb-row-${r.user_id}`}>
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold ${r.rank === 1 ? "bg-gradient-to-br from-amber-400 to-amber-600" : r.rank === 2 ? "bg-gradient-to-br from-slate-300 to-slate-500" : r.rank === 3 ? "bg-gradient-to-br from-amber-700 to-amber-900" : "bg-white/10 text-[var(--text-mute)]"}`}>#{r.rank}</div>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium truncate">{r.user_name}</div>
                    <div className="text-[10px] text-[var(--text-mute)]">Quiz {r.quiz_avg}% ({r.quiz_count}) • Asgn {r.asg_avg} ({r.asg_count}) • Att {r.attendance}</div>
                  </div>
                  <div className="font-display text-lg font-semibold text-fuchsia-300">{r.score}</div>
                </div>
              ))}
            </div>
          </div>
        </TabsContent>

        <TabsContent value="certificates">
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {enrollments.length === 0 && <Empty msg="Course complete karke certificate paayein." />}
            {enrollments.map((e) => {
              const eligible = e.status === "completed" || (e.progress || 0) >= 100;
              return (
                <div key={e.id} className="glass-strong rounded-2xl p-5" data-testid={`cert-card-${e.id}`}>
                  <Award className={`w-8 h-8 ${eligible ? "text-amber-300" : "text-[var(--text-mute)]"}`} />
                  <div className="font-display text-base font-semibold mt-3">{e.course_title}</div>
                  <div className="text-xs text-[var(--text-mute)] mt-1">Progress {e.progress || 0}%</div>
                  <div className="mt-1.5 h-1.5 rounded-full bg-white/5 overflow-hidden">
                    <div className="h-full bg-gradient-to-r from-amber-400 to-amber-600" style={{ width: `${e.progress || 0}%` }} />
                  </div>
                  <button
                    onClick={() => downloadCertificate(e.id)}
                    disabled={!eligible}
                    className="btn-primary w-full justify-center mt-4 text-xs disabled:opacity-40 disabled:cursor-not-allowed"
                    data-testid={`download-cert-${e.id}`}
                  >
                    <Download className="w-4 h-4" /> {eligible ? "Download certificate" : "Complete course to unlock"}
                  </button>
                </div>
              );
            })}
          </div>
        </TabsContent>

        <TabsContent value="payments">
          <div className="space-y-3">
            {payments.length === 0 && <Empty msg="No payments yet." />}
            {payments.map((p) => (
              <div key={p.id} className="glass rounded-xl p-4 flex items-center justify-between gap-3 flex-wrap" data-testid={`payment-${p.id}`}>
                <div>
                  <div className="text-sm font-semibold font-mono">{p.order_id}</div>
                  <div className="text-xs text-[var(--text-mute)] mt-0.5">{new Date(p.created_at).toLocaleString()} • {p.payment_mode || (p.via === "manual" ? "manual" : "online")}</div>
                </div>
                <div className="flex items-center gap-3">
                  <div className="text-right">
                    <div className="font-semibold">₹{(p.amount / 100).toLocaleString("en-IN")}</div>
                    <div className="text-xs"><span className={`px-2 py-0.5 rounded-full ${p.status === "paid" ? "bg-fuchsia-500/15 text-fuchsia-300" : "bg-amber-500/15 text-amber-300"}`}>{p.status}</span></div>
                  </div>
                  {p.status === "paid" && (
                    <button onClick={() => downloadInvoice(p.order_id)} className="btn-ghost text-xs" data-testid={`download-invoice-${p.id}`} title="Download invoice">
                      <Download className="w-4 h-4" /> Invoice
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        </TabsContent>

        <TabsContent value="alerts">
          <div className="space-y-3">
            {notifications.length === 0 && <Empty msg="No notifications." />}
            {notifications.map((n) => (
              <div key={n.id} className="glass rounded-xl p-4">
                <div className="flex items-start gap-2">
                  <Bell className="w-4 h-4 text-fuchsia-300 mt-0.5" />
                  <div className="flex-1">
                    <div className="text-sm font-semibold">{n.title}</div>
                    <div className="text-xs text-[var(--text-dim)] mt-1">{n.body}</div>
                    <div className="text-[10px] text-[var(--text-mute)] mt-2">{new Date(n.created_at).toLocaleString()}</div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </TabsContent>

        <TabsContent value="profile">
          <MyProfileTab />
        </TabsContent>

        <TabsContent value="shares">
          <SharesTab />
        </TabsContent>

        <TabsContent value="chat">
          <ChatTab />
        </TabsContent>

        <TabsContent value="bookmarks">
          <MyBookmarksTab />
        </TabsContent>

        <TabsContent value="qr">
          <ScanQRTab />
        </TabsContent>
      </Tabs>

      <div className="mt-10 grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
        <Promo icon={Trophy} t="Leaderboard" d="Climb your batch ranking by attending classes & topping quizzes." />
        <Promo icon={Award} t="Certificates" d="Earn a verifiable certificate after completing each course." />
        <Promo icon={MapPin} t="Visit center" d="Apollo Square, RS Bhandari Marg, Indore — for offline batches." />
      </div>

      <SubmitAssignmentDialog asgn={submitAsgn} onClose={() => setSubmitAsgn(null)} onDone={reload} />
      <TakeQuizDialog quiz={takeQuiz} onClose={() => setTakeQuiz(null)} onDone={reload} />
      <DoubtBotWidget />
    </div>
  );
}

function SubmitAssignmentDialog({ asgn, onClose, onDone }) {
  const [content, setContent] = useState("");
  const [url, setUrl] = useState("");
  const [submitting, setSubmitting] = useState(false);
  useEffect(() => { setContent(""); setUrl(""); }, [asgn]);
  if (!asgn) return null;
  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await api.post(`/assignments/${asgn.id}/submit`, { content, attachment_url: url || null });
      toast.success("Submitted!");
      onClose(); onDone();
    } catch (er) { toast.error(er.response?.data?.detail || "Failed"); }
    finally { setSubmitting(false); }
  };
  return (
    <Dialog open={!!asgn} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="bg-[var(--bg-elev-2)] border-white/10 max-w-xl">
        <DialogHeader><DialogTitle className="font-display">Submit — {asgn.title}</DialogTitle></DialogHeader>
        <form onSubmit={submit} className="space-y-3" data-testid="submit-asgn-form">
          <div className="text-xs text-[var(--text-dim)]">{asgn.description}</div>
          <label className="block">
            <div className="text-xs text-[var(--text-mute)] mb-1">Your answer / notes *</div>
            <textarea required value={content} onChange={(e) => setContent(e.target.value)} rows={6} className="w-full bg-[var(--bg-elev-1)] border border-white/10 rounded-xl p-3 text-sm" placeholder="Type your submission…" data-testid="asgn-content" />
          </label>
          <label className="block">
            <div className="text-xs text-[var(--text-mute)] mb-1">Attachment URL (optional)</div>
            <input value={url} onChange={(e) => setUrl(e.target.value)} className="w-full bg-[var(--bg-elev-1)] border border-white/10 rounded-xl px-3 py-2 text-sm" placeholder="https://drive.google.com/…" />
          </label>
          <button disabled={submitting} className="btn-primary w-full justify-center disabled:opacity-60" data-testid="asgn-submit">
            {submitting ? "Submitting…" : "Submit assignment"}
          </button>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function TakeQuizDialog({ quiz, onClose, onDone }) {
  const [answers, setAnswers] = useState({});
  const [result, setResult] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  useEffect(() => { setAnswers({}); setResult(null); }, [quiz]);
  if (!quiz) return null;
  const allAnswered = quiz.questions.every((q) => answers[q.id] !== undefined);
  const submit = async () => {
    setSubmitting(true);
    try {
      const { data } = await api.post(`/quizzes/${quiz.id}/submit`, { answers });
      setResult(data);
      toast.success(data.passed ? `Passed! ${data.percentage}%` : `Score: ${data.percentage}%`);
      onDone();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    finally { setSubmitting(false); }
  };
  return (
    <Dialog open={!!quiz} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="bg-[var(--bg-elev-2)] border-white/10 max-w-2xl max-h-[85vh] overflow-y-auto">
        <DialogHeader><DialogTitle className="font-display">{quiz.title}</DialogTitle></DialogHeader>
        {!result ? (
          <div className="space-y-4" data-testid="quiz-form">
            <div className="text-xs text-[var(--text-mute)]">{quiz.questions.length} questions • {quiz.duration_minutes} min • Pass {quiz.pass_percentage}%</div>
            {quiz.questions.map((q, qi) => (
              <div key={q.id} className="glass rounded-xl p-4">
                <div className="text-sm font-semibold mb-3">Q{qi + 1}. {q.text}</div>
                <div className="space-y-2">
                  {q.options.map((opt, oi) => (
                    <label key={oi} className={`flex items-center gap-2 cursor-pointer rounded-lg px-3 py-2 border ${answers[q.id] === oi ? "border-fuchsia-400/50 bg-fuchsia-500/10" : "border-white/10 hover:bg-white/5"}`} data-testid={`quiz-q${qi}-o${oi}`}>
                      <input type="radio" name={q.id} checked={answers[q.id] === oi} onChange={() => setAnswers({ ...answers, [q.id]: oi })} className="accent-fuchsia-500" />
                      <span className="text-sm">{opt}</span>
                    </label>
                  ))}
                </div>
              </div>
            ))}
            <button onClick={submit} disabled={!allAnswered || submitting} className="btn-primary w-full justify-center disabled:opacity-50" data-testid="quiz-submit">
              {submitting ? "Scoring…" : allAnswered ? "Submit quiz" : `Answer all (${Object.keys(answers).length}/${quiz.questions.length})`}
            </button>
          </div>
        ) : (
          <div className="space-y-4" data-testid="quiz-result">
            <div className={`glass-strong rounded-2xl p-5 text-center ${result.passed ? "border-fuchsia-400/30" : "border-amber-400/30"} border`}>
              <Sparkles className={`w-10 h-10 mx-auto ${result.passed ? "text-fuchsia-300" : "text-amber-300"}`} />
              <div className="font-display text-3xl font-semibold mt-2">{result.percentage}%</div>
              <div className="text-sm text-[var(--text-mute)] mt-1">{result.score} / {result.total_marks} marks</div>
              <div className={`mt-2 inline-block px-3 py-1 rounded-full text-xs ${result.passed ? "bg-fuchsia-500/15 text-fuchsia-300" : "bg-amber-500/15 text-amber-300"}`}>
                {result.passed ? "Passed ✓" : "Try again"}
              </div>
            </div>
            <div className="space-y-2">
              {result.breakdown.map((b, i) => {
                const q = quiz.questions[i];
                return (
                  <div key={b.question_id} className="glass rounded-lg p-3">
                    <div className="flex items-start gap-2">
                      {b.correct ? <Check className="w-4 h-4 text-fuchsia-300 mt-0.5 shrink-0" /> : <XIcon className="w-4 h-4 text-rose-300 mt-0.5 shrink-0" />}
                      <div className="flex-1">
                        <div className="text-xs font-semibold">{q.text}</div>
                        <div className="text-[11px] text-[var(--text-mute)] mt-1">Your answer: {q.options[b.picked] ?? "—"} • Correct: <span className="text-fuchsia-300">{q.options[b.correct_index]}</span></div>
                        {b.explanation && <div className="text-[11px] text-amber-200/80 mt-1">{b.explanation}</div>}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
            <button onClick={onClose} className="btn-ghost w-full justify-center">Close</button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

function Stat({ label, value }) {
  return (
    <div className="glass rounded-xl px-4 py-2">
      <div className="text-xs text-[var(--text-mute)]">{label}</div>
      <div className="font-display text-lg font-semibold">{value}</div>
    </div>
  );
}

function Row({ icon: Icon, text }) {
  return <div className="inline-flex items-center gap-1.5"><Icon className="w-4 h-4 text-fuchsia-300" /> {text}</div>;
}

function Empty({ msg }) { return <div className="glass rounded-2xl p-8 text-center text-[var(--text-dim)]">{msg}</div>; }

function ScheduleView({ classes }) {
  const [openClassId, setOpenClassId] = useState(null);
  if (!classes.length) return <Empty msg="Schedule abhi available nahi hai — admin se contact karein." />;
  const now = Date.now();
  // Group by month-year
  const groups = {};
  classes.forEach((c) => {
    const d = new Date(c.scheduled_at);
    const key = d.toLocaleString("default", { month: "long", year: "numeric" });
    if (!groups[key]) groups[key] = [];
    groups[key].push(c);
  });
  return (
    <div className="space-y-5" data-testid="full-schedule">
      <div className="text-sm text-[var(--text-dim)]">Total <b className="text-fuchsia-300">{classes.length}</b> classes scheduled in your batch.</div>
      {Object.entries(groups).map(([month, list]) => (
        <div key={month}>
          <div className="text-xs uppercase tracking-[0.18em] text-fuchsia-300 mb-2">{month}</div>
          <div className="space-y-2">
            {list.map((c) => {
              const dt = new Date(c.scheduled_at);
              const isPast = dt.getTime() + (c.duration_minutes || 60) * 60000 < now;
              const isLive = !isPast && dt.getTime() <= now + 5 * 60000;
              const isFuture = dt.getTime() > now;
              return (
                <div key={c.id} className={`glass rounded-xl p-4 ${isPast ? 'opacity-70' : ''}`} data-testid={`sched-${c.id}`}>
                  <div className="flex items-start justify-between gap-3 flex-wrap">
                    <div className="min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        {c.day_number && <span className="text-[10px] px-2 py-0.5 rounded-full bg-fuchsia-500/15 text-fuchsia-300">Day {c.day_number}</span>}
                        {isLive && <span className="text-[10px] px-2 py-0.5 rounded-full bg-rose-500/15 text-rose-300 uppercase">Live now</span>}
                        {isPast && <span className="text-[10px] px-2 py-0.5 rounded-full bg-white/10 text-white/60 uppercase">Completed</span>}
                        {isFuture && !isLive && <span className="text-[10px] px-2 py-0.5 rounded-full bg-amber-500/15 text-amber-300 uppercase">Upcoming</span>}
                      </div>
                      <div className="text-sm font-semibold mt-1.5 break-words">{c.title}</div>
                      <div className="text-xs text-[var(--text-mute)] mt-1">
                        {dt.toLocaleString()} • {c.duration_minutes} min
                        {c.topic ? ` • ${c.topic}` : ""}
                      </div>
                      {c.description && <div className="text-xs text-[var(--text-dim)] mt-1.5">📝 {c.description}</div>}
                    </div>
                    <div className="flex flex-col gap-2 items-end">
                      {(isLive || (isFuture && dt.getTime() - now < 15 * 60000)) && (
                        <Link to={`/live/${c.id}`} className="btn-primary text-xs"><PlayCircle className="w-4 h-4" /> Join</Link>
                      )}
                      <button onClick={() => setOpenClassId(openClassId === c.id ? null : c.id)} className="btn-ghost text-xs">
                        <FileText className="w-4 h-4" /> Notes {c.notes_count ? `(${c.notes_count})` : ""}
                      </button>
                    </div>
                  </div>
                  {openClassId === c.id && <ClassNotesList classId={c.id} />}
                </div>
              );
            })}
          </div>
        </div>
      ))}
    </div>
  );
}

function ClassNotesList({ classId }) {
  const [notes, setNotes] = useState(null);
  useEffect(() => {
    api.get(`/classes/${classId}/notes`).then(({ data }) => setNotes(data)).catch(() => setNotes([]));
  }, [classId]);
  if (notes === null) return <div className="mt-3 text-xs text-[var(--text-mute)]">Loading notes…</div>;
  if (notes.length === 0) return <div className="mt-3 text-xs text-[var(--text-mute)]">No notes uploaded for this class yet.</div>;
  return (
    <div className="mt-3 border-t border-white/5 pt-3 space-y-1.5">
      {notes.map((n) => (
        <a key={n.id} href={`${process.env.REACT_APP_BACKEND_URL}/api/notes/file/${n.stored_filename}?token=${localStorage.getItem("token")}`}
           target="_blank" rel="noreferrer"
           className="block glass rounded-lg px-3 py-2 hover:bg-white/5 text-xs flex items-center justify-between gap-2"
           data-testid={`note-${n.id}`}
           onClick={(e) => {
             // axios-style auth: download via fetch with header instead
             e.preventDefault();
             const url = `${process.env.REACT_APP_BACKEND_URL}/api/notes/file/${n.stored_filename}`;
             fetch(url, { headers: { Authorization: `Bearer ${localStorage.getItem("token")}` } })
               .then((r) => r.blob()).then((b) => {
                 const u = URL.createObjectURL(b);
                 const a = document.createElement("a");
                 a.href = u; a.download = n.filename; a.click();
                 setTimeout(() => URL.revokeObjectURL(u), 1000);
               });
           }}>
          <span className="inline-flex items-center gap-2 min-w-0">
            <FileText className="w-3.5 h-3.5 text-fuchsia-300 shrink-0" />
            <span className="truncate">{n.title}</span>
            <span className="text-[10px] text-[var(--text-mute)] uppercase">{n.ext}</span>
          </span>
          <span className="text-[10px] text-[var(--text-mute)]">{(n.size_bytes / 1024).toFixed(0)} KB</span>
        </a>
      ))}
    </div>
  );
}

function Promo({ icon: Icon, t, d }) {
  return (
    <div className="glass rounded-2xl p-5">
      <Icon className="w-5 h-5 text-fuchsia-300" />
      <div className="font-display text-base font-semibold mt-2">{t}</div>
      <div className="text-xs text-[var(--text-dim)] mt-1">{d}</div>
    </div>
  );
}
